import { RyuIcon, RyuPad, RyuText } from '@ramp/ryu'
import { Fragment, type ReactNode } from 'react'
import { styled } from 'styled-components'
import poshmarkLogo from '../../images/logos/poshmark.svg'
import poshmarkHero from '../../images/testimonials/poshmark.webp'
import bakedByMelissaHero from '../../images/testimonials/baked-by-melissa.webp'
import bakedByMelissaLogo from '../../images/logos/baked-by-melissa-logo.webp'
import boysAndGirlsClubLogo from '../../images/logos/BoysAndGirlsClubOfAmerica.svg'
import boysAndGirlsClubHero from '../../images/testimonials/BoysAndGirlsClubOfAmericaHero.webp'
import virginVoyagesLogo from '../../images/logos/virgin-voyages-logo.webp'
import virginVoyagesHero from '../../images/testimonials/virgin-voyages.webp'
import exactPartnersLogo from '../../images/logos/exact-logo.webp'
import exactPartnersHero from '../../images/testimonials/exact.webp'
import accountingAllStarsHero from '../../images/testimonials/accounting-all-star.webp'
import decimalLogo from '../../images/logos/decimal.webp'
import notionLogo from '../../images/logos/notion-logo.svg'
import notionHero from '../../images/testimonials/notion-hero.webp'
import { TestimonialSidebar } from './TestimonialSidebar'

const IconItemList = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`

const IconItem = styled(RyuIcon)`
  margin: 0 !important;
`

type CopyVariant = {
  title: ReactNode
  description: ReactNode
  sidebar?: ReactNode
  createCoreLead: boolean
  queue: string
  discoveryMethod?:
    | 'contact_sales'
    | 'partner_landing_page'
    | 'abm_gifting_yeti'
    | 'abm_gifting_baked_by_melissa'
    | 'directmail_lego_gift_2026_01'
    | 'enterprise_landing_page'
    | 'partner_cpm'
    | 'partner_meeting'
    | 'lifecycle_meeting'
    | 'cpm_gifting_sweets'
    | 'givefront_tier1'
    | 'givefront_tier2'
  ctaText?: string
}

export const copyVariants: Record<string, CopyVariant> = {
  lifecycle: {
    createCoreLead: true,
    queue: 'sdr-queue',
    title: 'Let’s get you connected',
    discoveryMethod: 'lifecycle_meeting',
    description: (
      <Fragment>
        <IconItemList>
          <IconItem type='help-circle' />
          <RyuText.div align='left' size='m' color='hushed'>
            Answers to any questions you have about Ramp
          </RyuText.div>
        </IconItemList>
        <IconItemList>
          <IconItem type='dollar-sign' />
          <RyuText.div align='left' size='m' color='hushed'>
            A custom assessment to see how much Ramp will save you
          </RyuText.div>
        </IconItemList>
      </Fragment>
    ),
  },
  'partner-cpm': {
    createCoreLead: true,
    queue: 'cpm-calendar',
    sidebar: (
      <TestimonialSidebar
        quoteText='&ldquo;Ramp feels like a true extension of our firm — solving issues, helping with edge cases, showing up when it matters.&rdquo;'
        quoteAuthorName='Dan Spada'
        quoteAuthorTitle='CPA, Partner at Exact Partners'
        stats={[
          {
            title: '$10B+',
            description: 'dollars saved',
          },
          {
            title: '27.5M+',
            description: 'hours saved',
          },
        ]}
        imageUrl={exactPartnersHero}
        logoImageUrl={exactPartnersLogo}
      />
    ),
    title: 'Let’s get you connected',
    description: <RyuPad />,
    discoveryMethod: 'partner_cpm',
  },
  'partner-meeting': {
    createCoreLead: true,
    queue: 'cpm-calendar',
    sidebar: (
      <TestimonialSidebar
        quoteText='&ldquo;Gone are the days of dreading expense reports and struggling to keep track of receipts. Overall, Ramp has positively impacted our day-to-day operations, saving us time and effort while ensuring greater control and visibility over our finances. We feel all of this ourselves and for our clients.&rdquo;'
        quoteAuthorName='Matt Tait'
        quoteAuthorTitle='CEO and Co-founder, Decimal'
        stats={[
          {
            title: '$10B+',
            description: 'dollars saved',
          },
          {
            title: '27.5M',
            description: 'hours saved',
          },
        ]}
        imageUrl={accountingAllStarsHero}
        logoImageUrl={decimalLogo}
        gradientStrength={0.5}
      />
    ),
    title: "Let's get you connected",
    description: <RyuPad />,
    discoveryMethod: 'partner_meeting',
  },
  'abm-baked-by-melissa': {
    createCoreLead: true,
    queue: 'sdr-queue',
    title: 'Let’s get you connected',
    description: <RyuPad />,
    discoveryMethod: 'abm_gifting_baked_by_melissa',
    sidebar: (
      <TestimonialSidebar
        quoteText='&ldquo;Ramp gives us real-time visibility into spending across the business, simplifies compliance at scale, and continually releases features that streamline back-office operations. Their team is a true partner.&rdquo;'
        quoteAuthorName='Melissa Ben-Ishay'
        quoteAuthorTitle='CEO & Co-Founder, Baked by Melissa'
        stats={[
          {
            title: '50,000+',
            description: 'customers run on Ramp',
          },
          {
            title: 'Over $1 million',
            description: 'in fraud prevented to date',
          },
        ]}
        imageUrl={bakedByMelissaHero}
        logoImageUrl={bakedByMelissaLogo}
      />
    ),
  },
  'cpm-gifting-sweets': {
    createCoreLead: true,
    queue: 'cpm-sweets',
    title: 'Let’s get you connected',
    description: <RyuPad />,
    discoveryMethod: 'cpm_gifting_sweets',
    sidebar: (
      <TestimonialSidebar
        quoteText='&ldquo;Ramp gives us real-time visibility into spending across the business, simplifies compliance at scale, and continually releases features that streamline back-office operations. Their team is a true partner.&rdquo;'
        quoteAuthorName='Melissa Ben-Ishay'
        quoteAuthorTitle='CEO & Co-Founder, Baked by Melissa'
        stats={[
          {
            title: '50,000+',
            description: 'customers run on Ramp',
          },
          {
            title: 'Over $1 million',
            description: 'in fraud prevented to date',
          },
        ]}
        imageUrl={bakedByMelissaHero}
        logoImageUrl={bakedByMelissaLogo}
      />
    ),
  },
  'abm-yeti': {
    createCoreLead: true,
    queue: 'sdr-queue',
    title: <Fragment>Let’s get you connected</Fragment>,
    description: <RyuPad />,
    discoveryMethod: 'abm_gifting_yeti',
    sidebar: (
      <TestimonialSidebar
        quoteText='&ldquo;We’ve simplified our workflows while improving accuracy, and we are faster in closing with the help of automation. We could not have achieved this without the solutions Ramp brought to the table.&rdquo;'
        quoteAuthorName='Kaustubh Khandelwal'
        quoteAuthorTitle='SVP of Finance'
        stats={[
          {
            title: '7 months',
            description: 'to achieve free cash flow goals instead of 12 months',
          },
          {
            title: '50% reduction',
            description: 'on time to month-end close',
          },
        ]}
        imageUrl={poshmarkHero}
        logoImageUrl={poshmarkLogo}
      />
    ),
  },
  'givefront-tier1': {
    createCoreLead: true,
    queue: 'givefront1',
    title: <Fragment>Schedule time with a Product Specialist</Fragment>,
    description: <RyuPad />,
    discoveryMethod: 'givefront_tier1',
    sidebar: (
      <TestimonialSidebar
        quoteText='&ldquo;A great impact of Ramp has been the time-savings. For my colleagues outside of finance, we’ve shortened the time commitment for submitting expense reports and have allowed them to repurpose that time so they can focus more on our local clubs and the youth we serve in our mission.&rdquo;'
        quoteAuthorName='Jason Penegar'
        quoteAuthorTitle='VP Controller'
        stats={[
          {
            title: '40 hours',
            description: 'saved per month for finance team',
          },
          {
            title: '50,000+',
            description: 'customers run on Ramp',
          },
        ]}
        imageUrl={boysAndGirlsClubHero}
        logoImageUrl={boysAndGirlsClubLogo}
      />
    ),
    ctaText: 'Book now',
  },
  'givefront-tier2': {
    createCoreLead: true,
    queue: 'givefront2',
    title: <Fragment>Sign up for a transition webinar</Fragment>,
    description: <RyuPad />,
    discoveryMethod: 'givefront_tier2',
    sidebar: (
      <TestimonialSidebar
        quoteText='&ldquo;A great impact of Ramp has been the time-savings. For my colleagues outside of finance, we’ve shortened the time commitment for submitting expense reports and have allowed them to repurpose that time so they can focus more on our local clubs and the youth we serve in our mission.&rdquo;'
        quoteAuthorName='Jason Penegar'
        quoteAuthorTitle='VP Controller'
        stats={[
          {
            title: '40 hours',
            description: 'saved per month for finance team',
          },
          {
            title: '50,000+',
            description: 'customers run on Ramp',
          },
        ]}
        imageUrl={boysAndGirlsClubHero}
        logoImageUrl={boysAndGirlsClubLogo}
      />
    ),
    ctaText: 'Book now',
  },
  'partner-lp': {
    createCoreLead: true,
    queue: 'landing-page',
    title: <Fragment>Let’s get you connected</Fragment>,
    description: (
      <Fragment>
        <IconItemList>
          <IconItem type='help-circle' />
          <RyuText.div align='left' size='m' color='hushed'>
            Answers to any questions you have about Ramp
          </RyuText.div>
        </IconItemList>
        <IconItemList>
          <IconItem type='dollar-sign' />
          <RyuText.div align='left' size='m' color='hushed'>
            A custom assessment to see how much Ramp will save you
          </RyuText.div>
        </IconItemList>
      </Fragment>
    ),
    discoveryMethod: 'partner_landing_page',
  },
  'procurement-specialist': {
    createCoreLead: true,
    queue: 'p2p-concierge-queue',
    title: (
      <Fragment>
        Let’s get you connected
        <br />
        with a Procurement Specialist
      </Fragment>
    ),
    description: (
      <Fragment>
        <IconItemList>
          <IconItem type='help-circle' />
          <RyuText.div align='left' size='m' color='hushed'>
            Answers to any questions you have about Ramp
          </RyuText.div>
        </IconItemList>
        <IconItemList>
          <IconItem type='dollar-sign' />
          <RyuText.div align='left' size='m' color='hushed'>
            A custom assessment to see how much Ramp will save you
          </RyuText.div>
        </IconItemList>
        <IconItemList>
          <IconItem type='merge' />
          <RyuText.div align='left' size='m' color='hushed'>
            Learn how Ramp can help you streamline your procurement processes
          </RyuText.div>
        </IconItemList>
      </Fragment>
    ),
    discoveryMethod: 'contact_sales',
  },
  enterprise: {
    createCoreLead: true,
    queue: 'sdr-queue',
    title: (
      <Fragment>
        Learn what Ramp can do
        <br />
        for your organization
      </Fragment>
    ),
    description: <RyuPad />,
    discoveryMethod: 'enterprise_landing_page',
    sidebar: (
      <TestimonialSidebar
        quoteText="&ldquo;As a global organization, it's impossible to track every expense at scale. I need guardrails and automation to maintain the oversight I need, and Ramp has delivered.&rdquo;"
        quoteAuthorName='Michael Litwin'
        quoteAuthorTitle='VP Controller, Virgin Voyages'
        stats={[
          {
            title: '50,000+',
            description: 'customers on Ramp',
          },
          {
            title: 'Over $10B',
            description: 'saved for customers',
          },
        ]}
        imageUrl={virginVoyagesHero}
        logoImageUrl={virginVoyagesLogo}
      />
    ),
  },
  buildingblocks: {
    createCoreLead: true,
    queue: 'sdr-queue',
    title: "Let's get you connected",
    description: (
      <RyuText.div align='left' size='m' color='hushed'>
        See how you can keep building your business with Ramp
      </RyuText.div>
    ),
    discoveryMethod: 'directmail_lego_gift_2026_01',
    sidebar: (
      <TestimonialSidebar
        quoteText='&ldquo;Ramp is the only vendor that can service all of our employees across the globe in one unified system. They handle multiple currencies seamlessly, integrate with all of our accounting systems, and thanks to their customizable card and policy controls, we&rsquo;re compliant worldwide.&rdquo;'
        quoteAuthorName='Brandon Zell'
        quoteAuthorTitle='Chief Accounting Officer, Notion'
        stats={[
          {
            title: '94%',
            description: 'of transactions are compliant and in-policy',
          },
          {
            title: '75+ hours',
            description: 'saved by Notion finance teams every month',
          },
        ]}
        imageUrl={notionHero}
        logoImageUrl={notionLogo}
      />
    ),
  },
} as const
